import prebid from 'prebid.js'
import 'prebid.js/modules/aolBidAdapter'
import 'prebid.js/modules/appnexusBidAdapter'
import 'prebid.js/modules/audienceNetworkBidAdapter'
import 'prebid.js/modules/consentManagement'
import 'prebid.js/modules/criteoBidAdapter'
import 'prebid.js/modules/emx_digitalBidAdapter'
// Try old gumgum adapter with new prebid
//import 'prebid.js/modules/gumgumBidAdapter'
import './gumgumBidAdapter'
import 'prebid.js/modules/ixBidAdapter'
import 'prebid.js/modules/openxBidAdapter'
import 'prebid.js/modules/pubmaticBidAdapter'
import 'prebid.js/modules/rubiconBidAdapter'
import 'prebid.js/modules/sovrnBidAdapter'
import 'prebid.js/modules/tripleliftBidAdapter'
import 'prebid.js/modules/unrulyBidAdapter'
import 'prebid.js/modules/yieldmoBidAdapter'
import './s2sAdapter/S2SAdapter'
import 'prebid.js/modules/userId'
import 'prebid.js/modules/digiTrustIdSystem'

// Video
import 'prebid.js/modules/oneVideoBidAdapter'
import 'prebid.js/modules/pulsepointBidAdapter'
import './indexExchanceVideoAdapter/indexExchangeBidAdapter'

prebid.processQueue()

import {
  IAdServerTargeting,
  IBidResponse,
  IBidsBackHandler,
  IBidsBackHandlerResponse,
  IPrebid,
  IPrebidAdUnit,
  IPrebidAuction
} from '../../typings/IPrebid'
import { IBidRequest, IPrebidAdapter } from '../../typings/IPrebidAdapter'
import { AuctionHouse } from '../auctionHouse/AuctionHouse'
import CMP from '../cmp/cmp'
import EventManager from '../eventManager/EventManager'
import { doOnce } from '../helpers/doOnce'
import { queryParamHas } from '../helpers/getQueryParam'
import WebModel from '../models/WebModel'
import { Slot } from '../slots/Slot'
import AmazonAdapter from './amazonAdapter/AmazonAdapter'
import BidderSettings from './BidderSettings'
import { BidRequestAdjustments } from './BidRequestAdjustments'
import { PrebidConfig } from './PrebidConfig'
import { AuctionResults } from './AuctionResults'
import RoundelAdapter from './roundelAdapter/RoundelAdapter'

export const LONG_TIMEOUT = 2500 // ms
export const SHORT_TIMEOUT_MULTIPLIER = 0.8

export default class PrebidFacade {
  constructor(
    readonly model: WebModel,
    readonly cmp: CMP,
    private auctionHouse: AuctionHouse,
    private pbjs = window.pbjs
  ) {
    this.aliasBidders(pbjs)
    this.registerAdapters(model, cmp)
    this.addEventListeners(pbjs)
    pbjs.setConfig(new PrebidConfig(cmp))
    pbjs.bidderSettings = new BidderSettings(
      model.discrepancies,
      model.videoDiscrepancies
    )
  }

  /**
   * Given an array of slots, this will request bids for each adunit in one request.
   * @param slots- Array of Slots
   */
  async requestBids(slots: Array<Slot>, timeout: number): Promise<void> {
    /**
     * Prebid requests bids from our partners here. When the auction
     * is complete, the bidBackHandler is fired.
     *
     * Normally, prebid would wait for the timeout given before
     * firing the bidBackHandler. However, we have a bid pool
     * that may contain sufficient bids already.
     *
     * If we already have bids to use, we shorten the timeout
     * to speed things along. The bid responses being received
     * after the short timeout are still collected in the auctionHouse
     * for potential use on another slot.
     */
    return new Promise<void>((resolve) => {
      if (slots.length === 0) {
        return resolve()
      }

      // Skip requesting bids if ?test=placeholders
      if (queryParamHas('test', 'placeholders')) {
        return resolve(this.emitBidReadyEvent(slots, {}))
      }

      /**
       * This function will be called when either the the auction ends,
       * or our custom timeout has elapsed.  The content of the function
       * will only be run once per auction.
       */
      const bidsBackHandler: IBidsBackHandler = doOnce(
        (bids: IBidsBackHandlerResponse) => {
          /**  If Prebid calls this, cancel the short timeout callback */
          if (auctionTimeoutId) {
            clearTimeout(auctionTimeoutId)
          }
          resolve(this.emitBidReadyEvent(slots, bids))
        }
      )

      const adUnits = this.getAdUnits(slots)
      const bidRequests = this.pbjs.requestBids({
        adUnits,
        auctionId: 'web',
        timeout: LONG_TIMEOUT,
        bidsBackHandler
      })

      /**
       * Here we set the real timeout for the auctions.
       * If this timeout elapses, we'll take the bids that
       * have come back so far, and send them to google.  Any
       * bids that come back between this timeout and the
       * pbjs.requestBids' timeout will be added to the auctionHouse.
       */
      const auctionTimeoutId = setTimeout(() => {
        const bidsObject = this.setupBidsForAuction(bidRequests)
        bidsBackHandler(bidsObject)
      }, this.getShortTimeout(timeout))
    })
  }

  private getShortTimeout(timeout: number): number {
    /** Don't shorten the the timeout if there nothing in the auctionHouse */
    return this.auctionHouse.getSize() === 0
      ? timeout
      : Math.floor(timeout * SHORT_TIMEOUT_MULTIPLIER)
  }

  /**
   * Retrieves a copy of IPrebidAdUnits for each slot.
   * Modifies those adunits as necessary (add floors, etc)
   * @param slots - Array<Slot>
   */
  private getAdUnits(slots: Array<Slot>): Array<IPrebidAdUnit> {
    const allAdUnits: Array<IPrebidAdUnit> = []
    slots.map((slot) => {
      const adUnits = slot
        .adunits()
        .map((adunit) => removeInvalidlySizedPrebids(adunit, slot))
      const bidRequestAdjustments = new BidRequestAdjustments(
        this.model,
        slot.adUnitId
      )
      adUnits.forEach((adUnit) => {
        adUnit.bids = bidRequestAdjustments.adjustBids(adUnit.bids)
        allAdUnits.push(adUnit)
      })
      // NOTE: Will potentially uncomment this at a later date.
      // adUnit = this.removeBiddersAlreadyInAuctionHouse(slot.prebidAdunits())
    })

    return allAdUnits
  }

  /**
   * This is used when our fast timeout triggers.  We need to
   * convert the Array of IBidResponses to the form that
   * bidBackHandler would have returned so we can pass it to
   * this.emitBidReadyEvent(), which expects that
   * form (i.e. bidsBackHandlerResponse)
   * @param bidRequests
   */
  private setupBidsForAuction(
    bidRequests?: IPrebidAuction
  ): IBidsBackHandlerResponse {
    if (!bidRequests) {
      return {}
    }

    /**
     * Construct the return object that this.emitBidReadyEvent
     * expects for the 'bids' parameter
     */
    const bidsObject: IBidsBackHandlerResponse = {}
    bidRequests
      .getAdUnitCodes()
      .forEach((adUnitCode) => (bidsObject[adUnitCode] = { bids: [] }))

    /** WARNING: requestBids.getBidsReceived() is not part of the public API */
    const bids: Array<IBidResponse> = bidRequests.getBidsReceived()

    bids.forEach((bid) => {
      bidsObject[bid.adUnitCode].bids.push(bid)
    })

    return bidsObject
  }

  static countBiddersAboveFloor(alias: string, model: WebModel): string {
    const bids = window.pbjs.getBidResponsesForAdUnitCode(alias).bids
    /** Increment hb_count for all bidders above floor */
    let hbCount = 0
    for (const bid of bids) {
      if (bid.cpm >= model.floor) {
        hbCount++
      }
    }

    return hbCount.toString()
  }

  /* Will be used later

  // Given a prebidAdUnit, this function removes all bid requests for bidders that 
  // already have a valid bid in the pool for the adUnit
  private removeBiddersAlreadyInAuctionHouse(prebidAdUnit: IPrebidAdUnit): IPrebidAdUnit {
    const poolBidders = this.recievedBids.getBiddersForAdUnit(prebidAdUnit.code)

    prebidAdUnit.bids = prebidAdUnit.bids.filter((bid) => {
      return  poolBidders.indexOf(bid.bidder as Bidder) === -1
    })

    return prebidAdUnit
  }
  */

  private emitBidReadyEvent(
    slots: Array<Slot>,
    bidResponses?: IBidsBackHandlerResponse
  ): void {
    const auctionResultSet = slots.map(
      (slot) => new AuctionResults(this.model, slot, bidResponses)
    )
    EventManager.trigger(EventManager.events.bidReady, auctionResultSet)
  }

  private registerAdapter(adapter: IPrebidAdapter): void {
    this.pbjs.registerBidAdapter(() => adapter, adapter.code)
  }

  /**
   * Registers prebid's emitted events to publish across our own
   * EventManager. Registering them with our own EventManager helps
   * reduce the number of dependencies on PrebidFacade by other modules.
   */
  private addEventListeners(pbjs: IPrebid): boolean {
    pbjs.onEvent(EventManager.events.bidRequested, (e: IBidRequest) => {
      if (e.auctionId === 'web') {
        EventManager.trigger(EventManager.events.bidRequested, e)
      }
    })

    pbjs.onEvent(EventManager.events.bidResponse, (e: IBidResponse) => {
      if (e.auctionId === 'web') {
        EventManager.trigger(EventManager.events.bidResponse, e)
      }
    })

    pbjs.onEvent(EventManager.events.bidWon, (e: IBidResponse) => {
      if (e.auctionId === 'web') {
        EventManager.trigger(EventManager.events.bidWon, e)
      }
    })

    pbjs.onEvent(EventManager.events.bidTimeout, (e) => {
      EventManager.trigger(EventManager.events.bidTimeout, e)
    })

    pbjs.onEvent(EventManager.events.auctionEnd, (e) => {
      if (e.auctionId === 'web') {
        EventManager.trigger(EventManager.events.auctionEnd, e)
      }
    })

    return true
  }
  /**
   * If you understand how these help us, please document it
   * below.
   * http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.aliasBidder
   */
  private aliasBidders(pbjs: IPrebid): boolean {
    pbjs.aliasBidder('ix', 'indexExchange')
    pbjs.aliasBidder('gumgum', 'gumgum_icv')

    return true
  }

  /**
   * Amazon is a bidder in our auctions. Amazon is not supported
   * natively by Prebid. By registering this custom adapter here,
   * Prebid will request a bid from Amazon during the normal auction
   * cycle.
   *
   * If the model isn't flagged to support UAM, the adapter is not registered.
   */
  private registerAdapters(model: WebModel, cmp: CMP): boolean {
    if (model.uam) {
      this.registerAdapter(new AmazonAdapter(model, cmp))
    }

    this.registerAdapter(new RoundelAdapter(model, cmp))

    return true
  }

  static getTargeting(id: string): IAdServerTargeting {
    return window.pbjs.getAdserverTargetingForAdUnitCode(id)
  }
}

// Remove prebids whose `size` doesn't fit.
export function removeInvalidlySizedPrebids(
  prebidAdunit: IPrebidAdUnit,
  { sizes }: Pick<Slot, 'sizes'>
): IPrebidAdUnit {
  prebidAdunit.bids = prebidAdunit.bids.filter((bid) => {
    if (prebidAdunit.mediaTypes.video) {
      return true
    }
    if (!prebidAdunit.mediaTypes.banner) {
      return
    }
    if (!bid.size) {
      return true
    }

    return sizes.find((allowedSize) => {
      return (
        !!bid.size &&
        (allowedSize[0] === bid.size[0] && allowedSize[1] === bid.size[1])
      )
    })
  })

  return prebidAdunit
}
